<template>
  <div v-if="items.length" class="article-editors-choice">
    <h2 class="title">Выбор редакции</h2>

    <div v-if="mainItem" class="content-wrapper">
      <NuxtLink class="main-article" :to="mainItem.link">
        <div class="img-container">
          <img :src="mainItem.image" :alt="mainItem.name" />
        </div>
        <div class="text-content">
          <div class="date">{{ toMonthYearDateFormat(mainItem.publicationDate) }}</div>
          <h3 class="article-title">{{ mainItem.name }}</h3>
        </div>
        <button class="arrow-btn" tabindex="-1">
          <BaseIconSvoi icon="arrow/right-up" size="m" />
        </button>
      </NuxtLink>

      <div class="side-list">
        <template v-for="(item, index) in sideItems" :key="item.id">
          <NuxtLink class="side-item" :to="item.link">
            <div class="side-left">
              <div class="side-image-container">
                <img :src="item.image" :alt="item.name" />
              </div>
              <div class="side-content">
                <div class="side-date">{{ toMonthYearDateFormat(item.publicationDate) }}</div>
                <div class="side-title">{{ item.name }}</div>
              </div>
            </div>
            <button class="arrow-btn" tabindex="-1">
              <BaseIconSvoi icon="arrow/right-up" size="m" />
            </button>
          </NuxtLink>
          <div v-if="index < sideItems.length - 1" class="separator" />
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import type { ArticleMainItem } from '~/models/articles'
  import { toMonthYearDateFormat } from '~/shared/utils/date'

  const props = defineProps<{
    items: ArticleMainItem[]
  }>()

  const mainItem = computed(() => props.items[0] ?? null)
  const sideItems = computed(() => props.items.slice(1, 4))
</script>

<style lang="scss" scoped>
  .article-editors-choice {
    @extend %flex-column;

    gap: 32px;
    width: 100%;

    @include vp-767 {
      gap: 20px;
    }

    > .title {
      font-weight: 500;
      font-size: 32px;
      line-height: 1.25;
      margin: 0;

      @include vp-767 {
        font-size: 24px;
      }
    }

    > .content-wrapper {
      display: flex;
      gap: 12px;
      width: 100%;

      @include vp-767 {
        flex-direction: column;
        gap: 16px;
      }

      > .main-article {
        position: relative;
        width: 420px;
        height: 400px;
        border-radius: 20px;
        overflow: hidden;
        cursor: pointer;
        flex-shrink: 0;
        text-decoration: none;
        color: inherit;

        @include vp-767 {
          width: 100%;
          height: 300px;
        }

        &:hover {
          > .img-container > img {
            transform: scale(1.1);
          }
        }

        > .img-container {
          position: relative;
          width: 100%;
          height: 100%;

          > img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: all 0.6s ease;
          }
        }

        > .text-content {
          position: absolute;
          top: 24px;
          left: 24px;
          width: 344px;
          @extend %flex-column;

          gap: 9px;

          @include vp-767 {
            width: calc(100% - 48px);
          }

          > .date {
            font-size: 14px;
            line-height: 22px;
            color: $color-potato-10;
          }

          > .article-title {
            font-weight: 500;
            font-size: 24px;
            line-height: 1.33;
            color: $color-default-white;
            margin: 0;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;

            @include vp-767 {
              font-size: 20px;
              -webkit-line-clamp: 2;
            }
          }
        }

        > .arrow-btn {
          position: absolute;
          bottom: 24px;
          right: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px;
          background: $color-default-white;
          border: none;
          border-radius: 30px;
          cursor: pointer;
          color: $color-default-black;
        }
      }

      > .side-list {
        @extend %flex-column;

        justify-content: space-between;
        gap: 8px;
        flex: 1;

        > .side-item {
          display: flex;
          align-items: center;
          gap: 12px;
          border-radius: 16px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          text-decoration: none;
          color: inherit;

          @include vp-767 {
            padding: 12px;
          }

          &:hover {
            background: $main-back-color;

            .side-image-container img {
              transform: scale(1.1);
            }
          }

          > .side-left {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;

            @include vp-767 {
              gap: 12px;
            }

            > .side-image-container {
              position: relative;
              width: 115px;
              height: 115px;
              border-radius: 16px;
              overflow: hidden;
              flex-shrink: 0;

              @include vp-767 {
                width: 100px;
                height: 100px;
              }

              > img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                transition: all 0.6s ease;
              }
            }

            > .side-content {
              @extend %flex-column;

              justify-content: center;
              gap: 6px;
              flex: 1;

              > .side-date {
                font-size: 14px;
                line-height: 22px;
                color: $color-potato-60;
              }

              > .side-title {
                font-size: 18px;
                line-height: 1.44;
                color: $color-default-black;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;

                @include vp-767 {
                  font-size: 16px;
                  -webkit-line-clamp: 1;
                }
              }
            }
          }

          > .arrow-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px;
            background: $color-default-white;
            border: none;
            border-radius: 30px;
            cursor: pointer;
            flex-shrink: 0;
            color: $color-default-black;
          }
        }

        > .separator {
          width: 100%;
          height: 0;
          border-top: 1px solid $color-potato-10;
        }
      }
    }
  }
</style>
